import { useCallback, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import { AutoCompleteCompleteEvent } from 'primereact/autocomplete';

import useDebounce from '@/components/hooks/useDebounce';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

import {
  accountDetailsOptions,
  editShipments,
  searchAccountsOptions,
  searchEquipmentOptions,
  searchSitesOptions,
  setInProgress as setInProgressRest,
  shipmentCalvesOptions,
  shipmentCaneSuggestionOptions,
  shipmentDonorsOptions,
  shipmentEmbryosOptions,
  shipmentOptions,
  shipmentRecipientsOptions,
  shipmentSemenOptions,
  shippingSitesOptions,
  useEditShipments,
} from './index';
import type {
  AccountSearchResult,
  EditShipmentInput,
  EquipmentRef,
  SetInProgressResponse,
  ShipmentCalf,
  ShipmentCaneSuggestion,
  ShipmentDetail,
  ShipmentDonor,
  ShipmentEmbryo,
  ShipmentListItem,
  ShipmentRecipient,
  ShipmentSemen,
  SiteRef,
} from './types';

export function useShippingSites() {
  const token = useAccessToken();
  const restQuery = useQuery(shippingSitesOptions(token));

  return {
    data: restQuery.data,
    isLoading: restQuery.isLoading,
  };
}

export function useShipmentAccountDetails(
  accountId: string | null | undefined,
) {
  const token = useAccessToken();
  const restQuery = useQuery({
    ...accountDetailsOptions(token, accountId as string),
    enabled: !!token && !!accountId,
  });

  return {
    data: restQuery.data,
    isLoading: false,
    error: restQuery.error,
  };
}

function useShipmentSearch<T>(
  restQueryOptions: (
    token: AccessToken,
    query: string,
  ) => UseQueryOptions<T[], Error, T[], string[]>,
) {
  const token = useAccessToken();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery);

  const { data: restData } = useQuery({
    ...restQueryOptions(token, debouncedSearch),
    enabled: !!token && !!debouncedSearch,
  });

  const filteredResults = debouncedSearch ? restData : undefined;

  const search = useCallback((e: AutoCompleteCompleteEvent) => {
    setSearchQuery(e.query);
  }, []);

  const setFilteredResults = useCallback((value: T[] | undefined) => {
    if (value === undefined) setSearchQuery('');
  }, []);

  return { filteredResults, search, setFilteredResults };
}

export function useShipmentEquipmentSearch() {
  return useShipmentSearch<EquipmentRef>(searchEquipmentOptions);
}

export function useShipmentSiteSearch() {
  return useShipmentSearch<SiteRef>(searchSitesOptions);
}

export function useShipmentAccountSearch() {
  return useShipmentSearch<AccountSearchResult>(searchAccountsOptions);
}

export function useUpdateShipment() {
  const { mutateAsync: editShipments } = useEditShipments();

  const mutateAsync = async (
    rows: ShipmentListItem[],
    updates: Record<string, unknown>,
  ): Promise<
    Array<{ success: boolean; code: number; message: string | null }>
  > => {
    const inputs: EditShipmentInput[] = rows.map((row) => ({
      shipmentId: row.id,
      ...(updates as Omit<EditShipmentInput, 'shipmentId'>),
    }));
    return editShipments(inputs).then((results) =>
      results.map((r) => ({ ...r, message: r.message ?? null })),
    );
  };

  return { mutateAsync };
}

export function useShipmentDetail(id: string | null) {
  const token = useAccessToken();
  const restQuery = useQuery({
    ...shipmentOptions(token, id!),
    enabled: !!token && !!id,
  });

  return {
    data: restQuery.data as ShipmentDetail | undefined,
    refetch: restQuery.refetch,
  };
}

export function useShipmentEmbryos(id: string | null) {
  const token = useAccessToken();
  const restQuery = useQuery({
    ...shipmentEmbryosOptions(token, id!),
    enabled: !!token && !!id,
  });

  return {
    data: restQuery.data as ShipmentEmbryo[] | undefined,
    refetch: restQuery.refetch,
  };
}

export function useShipmentCaneSuggestion(id: string | null, enabled = true) {
  const token = useAccessToken();
  const isCaneSuggestionEnabled = useFeatureFlag(
    'Recane_Unique_Cane_Suggestion',
  );
  const queryEnabled = enabled && isCaneSuggestionEnabled && !!id;
  const restQuery = useQuery({
    ...shipmentCaneSuggestionOptions(token, id!),
    enabled: !!token && queryEnabled,
  });

  if (!queryEnabled) {
    return {
      data: undefined,
      isLoading: false,
      error: null,
    };
  }

  return {
    data: restQuery.data as ShipmentCaneSuggestion | undefined,
    isLoading: restQuery.isLoading,
    error: restQuery.error,
  };
}

export function useShipmentSemen(id: string | null) {
  const token = useAccessToken();
  const restQuery = useQuery({
    ...shipmentSemenOptions(token, id!),
    enabled: !!token && !!id,
  });

  return {
    data: restQuery.data as ShipmentSemen[] | undefined,
    refetch: restQuery.refetch,
  };
}

export function useShipmentDonors(id: string | null) {
  const token = useAccessToken();
  const restQuery = useQuery({
    ...shipmentDonorsOptions(token, id!),
    enabled: !!token && !!id,
  });

  return { data: restQuery.data as ShipmentDonor[] | undefined };
}

export function useShipmentRecipients(id: string | null) {
  const token = useAccessToken();
  const restQuery = useQuery({
    ...shipmentRecipientsOptions(token, id!),
    enabled: !!token && !!id,
  });

  return { data: restQuery.data as ShipmentRecipient[] | undefined };
}

export function useShipmentCalves(id: string | null) {
  const token = useAccessToken();
  const restQuery = useQuery({
    ...shipmentCalvesOptions(token, id!),
    enabled: !!token && !!id,
  });

  return { data: restQuery.data as ShipmentCalf[] | undefined };
}

export function useSaveShipment() {
  const token = useAccessToken();
  const mutateAsync = async (
    shipmentId: string,
    input: Omit<EditShipmentInput, 'shipmentId'>,
  ): Promise<void> => {
    const results = await editShipments(token, [{ shipmentId, ...input }]);
    const failed = results.find((r) => !r.success);
    if (failed) throw new Error(failed.message ?? 'Update failed');
  };

  return { mutateAsync };
}

export function useSetInProgress() {
  const token = useAccessToken();
  const refreshAdditionalData = useFeatureFlag(
    'Shipping_Additional_Data_Local_Time',
  );
  const queryClient = useQueryClient();

  const mutateAsync = async (
    shipmentId: string,
    packedById?: string | null,
  ): Promise<Pick<SetInProgressResponse, 'shipmentStatus' | 'packedById'>> => {
    const result = await setInProgressRest(token, shipmentId, packedById);
    if (refreshAdditionalData) {
      await queryClient.invalidateQueries({
        queryKey: shipmentOptions(token, shipmentId).queryKey,
      });
    }
    return result;
  };

  return { mutateAsync };
}
